import {Component, computed, effect, inject, OnDestroy, signal} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators
} from '@angular/forms';
import {Router, RouterLink} from '@angular/router';
import {finalize} from 'rxjs';
import {v4 as uuidv4} from 'uuid';
import {NzAlertModule} from 'ng-zorro-antd/alert';
import {NzButtonModule} from 'ng-zorro-antd/button';
import {NzCardModule} from 'ng-zorro-antd/card';
import {NzFormModule} from 'ng-zorro-antd/form';
import {NzIconModule} from 'ng-zorro-antd/icon';
import {NzInputModule} from 'ng-zorro-antd/input';
import {NzNotificationService} from 'ng-zorro-antd/notification';
import {NzSpinModule} from 'ng-zorro-antd/spin';
import {NzStepsModule} from 'ng-zorro-antd/steps';
import {NcalayerService} from '../../core/services/ncalayer.service';
import {NcalayerCancelledError} from '../../core/services/ncalayer-cancelled-error';
import {NcalayerUnavailableError} from '../../core/services/ncalayer-unavailable-error';
import {AuthFlowService, ConsentDocument} from '../../core/services/auth-flow.service';
import {EMAIL_VALIDATORS} from '../../shared/email-validator';
import {psdValidators, PSD_VALIDATORS} from '../../shared/psd-validator';
import {psdRequirements} from '../../shared/psd-requirements';
import {PsdRulesService} from '../../core/services/psd-rules.service';
import {ConsentView} from '../../shared/consent-view/consent-view';
import {textControl} from '../../shared/text-control';
import {psdMatchValidator} from '../../shared/psd-fields/psd-form';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';

@Component({
  selector: 'app-register',
  standalone: true,
  imports: [
    ConsentView,
    ReactiveFormsModule,
    RouterLink,
    NzAlertModule,
    NzButtonModule,
    NzCardModule,
    NzFormModule,
    NzInputModule,
    NzIconModule,
    NzSpinModule,
    NzStepsModule,
    TranslatePipe,
  ],
  templateUrl: './register.html',
  styleUrl: './register.css'
})
export class Register implements OnDestroy {
  private readonly authFlow = inject(AuthFlowService);
  private readonly ncalayer = inject(NcalayerService);
  private readonly notification = inject(NzNotificationService);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly psdRulesService = inject(PsdRulesService);

  protected errorMessage = '';
  protected isSigning = false;
  protected isVerifyingXml = false;
  protected isSubmitting = false;
  protected passwordVisible = false;
  protected confirmPasswordVisible = false;
  protected isSendingCode = false;
  protected codeSent = false;
  protected resendCountdown = 0;
  protected isConfirmingCode = false;
  protected emailVerified = false;
  protected currentStep = 0;

  protected readonly emailConfirmationRequired = signal(true);
  protected readonly consentDoc = signal<ConsentDocument | null>(null);
  protected readonly isLoadingConsent = signal(false);

  private countdownTimer?: ReturnType<typeof setInterval>;

  form = new FormGroup({
    xml: new FormControl('', {nonNullable: true, validators: [Validators.required]}),
    iin: new FormControl({value: '', disabled: true}, {
      nonNullable: true,
      validators: [Validators.required, Validators.pattern(/^\d{12}$/)]
    }),
    email: new FormControl('', {
      nonNullable: true,
      validators: EMAIL_VALIDATORS
    }),
    emailCode: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.pattern(/^\d{6}$/)]
    }),
    password: textControl(PSD_VALIDATORS),
    confirmPassword: textControl([Validators.required]),
  }, {validators: [psdMatchValidator('password', 'confirmPassword')]});

  protected readonly psdRules = this.psdRulesService.rules;
  protected readonly psdRequirements = computed(() => psdRequirements(this.psdRules()));

  constructor() {
    this.loadConsent();
    this.loadEmailConfirmationSettings();
    this.psdRulesService.load();

    effect(() => {
      const control = this.form.controls.password;
      control.setValidators(psdValidators(this.psdRules()));
      control.updateValueAndValidity({emitEvent: false});
    });
  }

  changeEmail(): void {
    this.emailVerified = false;
    this.errorMessage = '';
    this.codeSent = false;
    this.clearCountdown();
    this.resendCountdown = 0;
    this.form.controls.email.enable({emitEvent: false});
    this.form.controls.emailCode.reset('');
  }

  backToSignStep(): void {
    this.currentStep = 0;
    this.errorMessage = '';
  }

  ngOnDestroy(): void {
    this.clearCountdown();
  }

  signXml(): void {
    const consentDoc = this.consentDoc();
    if (!consentDoc) {
      return;
    }

    this.errorMessage = '';
    this.isSigning = true;

    this.ncalayer.signXml(this.generateXml(consentDoc.xml), false)
      .pipe(finalize(() => this.isSigning = false))
      .subscribe({
        next: (xml) => {
          this.verifyXml(xml);
        },
        error: (err) => {
          if (err instanceof NcalayerCancelledError) {
            this.notification.warning(
              this.translate.instant('ncalayer.cancelledTitle'),
              this.translate.instant('ncalayer.cancelledDesc')
            );
            return;
          }

          if (err instanceof NcalayerUnavailableError) {
            this.notification.warning(
              this.translate.instant('ncalayer.unavailableTitle'),
              this.translate.instant('ncalayer.unavailableDesc')
            );
            return;
          }

          this.errorMessage = this.translate.instant('register.toasts.signFailed');
        }
      });
  }

  sendEmailCode(): void {
    if (this.form.controls.email.invalid || this.resendCountdown > 0) {
      this.form.controls.email.markAsTouched();
      return;
    }

    const email = this.form.controls.email.value;
    this.errorMessage = '';
    this.isSendingCode = true;

    this.authFlow.sendEmailCode({email})
      .pipe(finalize(() => this.isSendingCode = false))
      .subscribe({
        next: () => {
          this.codeSent = true;
          this.form.controls.email.disable({emitEvent: false});
          this.startCountdown(120);
          this.notification.success(this.translate.instant('register.toasts.codeSentTitle'), this.translate.instant('register.toasts.codeSentDesc'));
        },
        error: () => {
          this.errorMessage = this.translate.instant('register.toasts.sendCodeFailed');
        }
      });
  }

  protected confirmCode(code: string): void {
    if (this.form.controls.email.invalid || this.isConfirmingCode || this.emailVerified) {
      return;
    }

    const email = this.form.controls.email.value;
    this.errorMessage = '';
    this.isConfirmingCode = true;

    this.authFlow.confirmEmailCode({email, code})
      .pipe(finalize(() => this.isConfirmingCode = false))
      .subscribe({
        next: () => {
          this.emailVerified = true;
          this.form.controls.email.disable({emitEvent: false});
          this.notification.success(this.translate.instant('register.toasts.emailConfirmedTitle'), this.translate.instant('register.toasts.emailConfirmedDesc'));
        },
        error: (err) => {
          this.emailVerified = false;
          this.errorMessage = err?.error?.message || err?.error?.detail || this.translate.instant('register.toasts.invalidCode');
        }
      });
  }

  submit(): void {
    if (this.emailConfirmationRequired() && !this.emailVerified) {
      this.errorMessage = this.translate.instant('register.toasts.confirmEmailFirst');
      return;
    }

    if (this.form.invalid) {
      Object.values(this.form.controls).forEach(control => {
        control.markAsDirty();
        control.markAsTouched();
        control.updateValueAndValidity();
      });
      this.errorMessage = this.form.hasError('psdMismatch')
        ? this.translate.instant('register.psdMismatch')
        : '';
      return;
    }

    const {xml, iin, password, confirmPassword, email, emailCode} = this.form.getRawValue();

    this.errorMessage = '';
    this.isSubmitting = true;

    this.authFlow.registerSsoUser({
      xml,
      iin,
      psd: password,
      confirmPsd: confirmPassword,
      email,
      emailCode,
    })
      .pipe(finalize(() => this.isSubmitting = false))
      .subscribe({
        next: () => {
          this.notification.success(this.translate.instant('register.toasts.registeredTitle'), this.translate.instant('register.toasts.registeredDesc'));
          this.router.navigate(['/login']);
        },
        error: (err) => {
          this.errorMessage = err?.error?.error || this.translate.instant('register.toasts.registerFailed');
        }
      });
  }

  private verifyXml(xml: string): void {
    this.isVerifyingXml = true;

    this.authFlow.verifyEcpXml({xml})
      .pipe(finalize(() => this.isVerifyingXml = false))
      .subscribe({
        next: (person) => {
          const iin = person.iin || person.bin || '';

          if (!iin) {
            this.errorMessage = this.translate.instant('login.iinNotFromCert');
            return;
          }

          this.form.controls.xml.setValue(xml);
          this.form.patchValue({
            iin
          });
          this.currentStep = 1;
          this.notification.success(this.translate.instant('register.toasts.edsConfirmedTitle'), this.translate.instant('register.toasts.edsConfirmedDesc'));
        },
        error: () => {
          this.errorMessage = this.translate.instant('register.toasts.certCheckFailed');
        }
      });
  }

  private startCountdown(seconds: number): void {
    this.clearCountdown();
    this.resendCountdown = seconds;
    this.countdownTimer = setInterval(() => {
      this.resendCountdown--;
      if (this.resendCountdown <= 0) {
        this.clearCountdown();
      }
    }, 1000);
  }

  private clearCountdown(): void {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = undefined;
    }
  }

  private generateXml(consentXml: string): string {
    return `<xml><documentType>Registration</documentType><ticketTime>${Date.now().toString()}</ticketTime><id>${uuidv4()}</id>${consentXml}</xml>`;
  }

  private loadEmailConfirmationSettings(): void {
    this.authFlow.emailConfirmationSettings().subscribe({
      next: (settings) => {
        this.emailConfirmationRequired.set(settings.requiredOnRegistration);
        if (!settings.requiredOnRegistration) {
          this.form.controls.emailCode.disable({emitEvent: false});
        }
      }
    });
  }

  private loadConsent(): void {
    this.errorMessage = '';
    this.isLoadingConsent.set(true);

    this.authFlow.consentDocument()
      .pipe(finalize(() => this.isLoadingConsent.set(false)))
      .subscribe({
        next: (doc) => this.consentDoc.set(doc),
        error: () => {
          this.errorMessage = this.translate.instant('consent.loadFailed');
        }
      });
  }

}
